import { useEffect, useLayoutEffect } from "react"
import { Navigate, Outlet, Route, Routes } from "react-router-dom"
import dayjs, { COUNTRY_TO_LOCALE_MAPPING } from "lib/dayjs"

import "react-toastify/dist/ReactToastify.css"
import "@mdxeditor/editor/style.css"
import "assets/scss/globals.scss"

import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { showTermsOfUseModal } from "store/modalSlise"

import { UserRole } from "utils/checkRoleType"
import { useCountry } from "hooks/useCountry"
import { useLoadContractorAccountStatuses } from "hooks/useLoadContractorAccountStatuses"
import { useUserProfile } from "hooks/useUserProfile"
import { Modals } from "modals"
import { AuthLayout } from "layouts/AuthLayout"
import MainLayout from "layouts/MainLayout/MainLayout"
import { NotFound } from "components/common/NotFound"
import { OAuthApp } from "components/common/OAuthApp"
import { PrivateRoute } from "components/common/PrivateRoute"
import {
  AccountSettingsLayout,
  ComingSoonPage,
  ContractorAdditionalInformationPage,
  ContractorPasswordPage,
  ContractorProfilePage,
  ContractorThemePage,
  EnterpriseAccountAdditionalInformationPage,
  EnterpriseAccountProfilePage,
  EnterpriseResetPasswordPage,
  EnterpriseThemePage,
  getDefaultAccountSettingsPath,
} from "pages/account/AccountSettingsPage"
import { ChangePassword } from "pages/common/Auth/ChangePassword"
import { CreatePassword } from "pages/common/Auth/CreatePassword"
import { ForgotPassword } from "pages/common/Auth/ForgotPassword"
import { SignIn } from "pages/common/Auth/SignIn"
import { RedirectPage } from "pages/common/RedirectPage"
import { ReferralLanding } from "pages/common/ReferralLanding"
import SampleErrorPage from "pages/common/SampleErrorPage"
import AdHocReportsPage from "pages/contractor/AdHocReportsPage"
import AlertsPage from "pages/contractor/AlertsPage/AlertsPage"
import ContractorDashboard from "pages/contractor/ContractorDashboard"
import ContractorDevicesPage from "pages/contractor/ContractorDevicesPage/ContractorDevicesPage"
import ContractorSensorsPage from "pages/contractor/ContractorSensorsPage/ContractorSensorsPage"
import { ContractorUsers } from "pages/contractor/ContractorUsers/ContractorUsers"
import {
  CustomerDashboardAlerts,
  CustomerDashboardDetails,
  CustomerDashboardLayout,
  CustomerDashboardLiveData,
  CustomerDashboardMessages,
  CustomerDashboardMonitoring,
  CustomerDashboardOverview,
  CustomerDashboardPerformanceTest,
  CustomerDashboardReports,
} from "pages/contractor/CustomerDashboard"
import { Customers } from "pages/contractor/Customers"
import IntelligenceReportsPage from "pages/contractor/IntelligenceReportsPage"
import { FullIntelligenceReport } from "pages/contractor/IntelligenceReportsPage/FullIntelligenceReport"
import { ReportRedirectBySn } from "pages/contractor/IntelligenceReportsPage/ReportRedirectBySn"
import { MessageCenter } from "pages/contractor/MessageCenter"
import { MessageHistory } from "pages/contractor/MessageHistory"
import { MessageReport } from "pages/contractor/MessageReport"
import { MessageTemplateAdd } from "pages/contractor/MessageTemplateAdd"
import MessageTemplateContainerLayout from "pages/contractor/MessageTemplateContainer/MessageTemplateContainerLayout"
import { MessageTemplateCreate } from "pages/contractor/MessageTemplateCreate"
import { MessageTemplateEdit } from "pages/contractor/MessageTemplateEdit"
import AlertRulesPage from "pages/contractor/MonitoringPage/AlertRulesPage"
import MonitoringPage from "pages/contractor/MonitoringPage/MonitoringPage"
import MonthlyEngagementPage from "pages/contractor/MonthlyEngagementPage"
import { NotificationsPage } from "pages/contractor/NotificationsPage"
import { Orders } from "pages/contractor/Orders"
import PerformanceTestHistoryPage from "pages/contractor/PerformanceTestHistoryPage/PerformanceTestHistoryPage"
import PerformanceTestPage from "pages/contractor/PerformanceTestPage/PerformanceTestPage"
import EnterpriseAlertsPage from "pages/enterprise/Alerts"
import EnterpriseDashboard from "pages/enterprise/Dashboard"
import EnterpriseDevicesPage from "pages/enterprise/Devices"
import EnterpriseSensorsPage from "pages/enterprise/Devices/Sensors"
import EnterpriseMonitoringPage from "pages/enterprise/Monitoring"
import EnterpriseOrdersPage from "pages/enterprise/Orders"
import { ReferralAgentDashboard } from "pages/referralAgent/Dashboard"
import { MyReferrals } from "pages/referralAgent/MyReferrals"
import { Alerts as VendorAlerts } from "pages/vendor/Alerts"
import { Calls } from "pages/vendor/Calls"
import { Contractors } from "pages/vendor/Contractors"
import { Dashboard as VendorDashboard } from "pages/vendor/Dashboard"
import { Devices as VendorDevices } from "pages/vendor/Devices"
import { MasterPass } from "pages/vendor/MasterPass"
import {
  VendorMessageCenter,
  VendorMessageHistory,
  VendorMessageReport,
  VendorMessageTemplate,
  VendorScheduledMessages,
} from "pages/vendor/MessageCenter"
import { Orders as VOrders } from "pages/vendor/Orders"
import { Referrals as VendorReferrals } from "pages/vendor/Referrals"
import VendorSensors from "pages/vendor/Sensors/Sensors"
import { Users } from "pages/vendor/Users"
import { WarehouseList } from "pages/vendor/WarehouseList"
import { Devices as WHDevices } from "pages/warehouse/Devices"
import { Orders as WHOrders } from "pages/warehouse/Orders"

export const App = () => {
  const isUserAuthorized = useAppSelector(state => state.user.isUserAuthorized)
  const dispatch = useAppDispatch()
  const country = useCountry()
  const { userProfile, userRole, isEnterprise, isSubscriptionPlanRestricted } = useUserProfile()
  const isAdHocReportsEnabled = useAppSelector(isFeatureEnabled(FeatureFlag.AdHocReports))
  const isEnterpriseIteration2Enabled = useAppSelector(isFeatureEnabled(FeatureFlag.EnterpriseIteration2))
  const isSensorsOrderingEnabled = useAppSelector(isFeatureEnabled(FeatureFlag.SensorsOrdering))

  useLoadContractorAccountStatuses()

  useEffect(() => {
    if (isUserAuthorized && userProfile?.terms !== true) {
      dispatch(showTermsOfUseModal(true))
    }
  }, [isUserAuthorized])

  useLayoutEffect(() => {
    const locale = COUNTRY_TO_LOCALE_MAPPING[country]
    dayjs.locale(locale)
  }, [country])

  const mainApp = (
    <MainLayout>
      <Routes>
        <Route
          element={
            <>
              <Outlet />
              <Modals />
            </>
          }
        >
          <Route path="auth" element={userProfile ? <Navigate to="/" replace /> : <AuthLayout />}>
            <Route path="sign-in" element={<SignIn />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="create-password" element={<CreatePassword />} />
          </Route>

          {/* Sample Error Route for Sentry Testing */}
          <Route path="sample-error" element={<SampleErrorPage />} />

          {/* Public referral registration page */}
          <Route path="r/:slug" element={<ReferralLanding />} />

          {/* Enterprise */}
          {isEnterprise && (
            <Route element={<PrivateRoute />}>
              <Route path="dashboard" element={<EnterpriseDashboard />} />
              {isEnterpriseIteration2Enabled && <Route path="devices" element={<EnterpriseDevicesPage />} />}
              {isEnterpriseIteration2Enabled && isSensorsOrderingEnabled && (
                <Route path="devices/sensors" element={<EnterpriseSensorsPage />} />
              )}
              {isEnterpriseIteration2Enabled && <Route path="orders" element={<EnterpriseOrdersPage />} />}
              {isEnterpriseIteration2Enabled && <Route path="alerts" element={<EnterpriseAlertsPage />} />}
              {isEnterpriseIteration2Enabled && <Route path="monitoring" element={<EnterpriseMonitoringPage />} />}
              <Route path="account" element={<AccountSettingsLayout />}>
                <Route index element={<Navigate to={getDefaultAccountSettingsPath(userRole)} replace />} />
                <Route path="profile" element={<EnterpriseAccountProfilePage />} />
                <Route path="additional-information" element={<EnterpriseAccountAdditionalInformationPage />} />
                <Route path="password" element={<EnterpriseResetPasswordPage />} />
                <Route path="theme" element={<EnterpriseThemePage />} />
                <Route path="*" element={<Navigate to={getDefaultAccountSettingsPath(userRole)} replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          )}

          {isSubscriptionPlanRestricted && (
            <Route element={<PrivateRoute />}>
              <Route path="/" element={<MessageCenter hideUnavailableModal />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          )}

          {/* CONTRACTOR */}
          {[UserRole.ContractorAdmin, UserRole.ContractorManager, UserRole.ContractorEngineer].includes(userRole) &&
            !isSubscriptionPlanRestricted && (
              <Route element={<PrivateRoute />}>
                <Route path="dashboard" element={<ContractorDashboard />} />
                <Route path="devices" element={<ContractorDevicesPage />} />
                <Route path="devices/sensors" element={<ContractorSensorsPage />} />
                <Route path="orders" element={<Orders />}></Route>
                <Route path="customers" element={<Customers />} />
                <Route path="customers/:id/*" element={<CustomerDashboardLayout />}>
                  <Route index element={<CustomerDashboardOverview />} />
                  <Route path="overview" element={<CustomerDashboardOverview />} />
                  <Route path="details" element={<CustomerDashboardDetails />} />
                  <Route path="monitoring" element={<CustomerDashboardMonitoring />} />
                  <Route path="livedata" element={<CustomerDashboardLiveData />} />
                  <Route path="alerts" element={<CustomerDashboardAlerts />} />
                  <Route path="messages" element={<CustomerDashboardMessages />} />
                  <Route path="perf-test" element={<CustomerDashboardPerformanceTest />} />
                  <Route path="reports" element={<CustomerDashboardReports />} />
                </Route>
                <Route path="alerts" element={<AlertsPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="monthly-engagement" element={<Navigate to="/engagement-report" replace />} />
                <Route path="engagement-report" element={<MonthlyEngagementPage />} />
                <Route path="perf-test" element={<PerformanceTestPage />} />
                <Route path="perf-test-history" element={<PerformanceTestHistoryPage />} />
                <Route path="intelligence-reports" element={<IntelligenceReportsPage />} />
                <Route path="intelligence-reports/:reportId/full" element={<FullIntelligenceReport />} />
                {isAdHocReportsEnabled && <Route path="ad-hoc-reports" element={<AdHocReportsPage />} />}
                <Route path="reports/:deviceSN" element={<ReportRedirectBySn />} />
                <Route path="monitoring" element={<MonitoringPage />} />
                <Route path="monitoring/alert-rules" element={<AlertRulesPage />} />
                <Route path="account" element={<AccountSettingsLayout />}>
                  <Route index element={<Navigate to={getDefaultAccountSettingsPath(userRole)} replace />} />
                  <Route path="profile" element={<ContractorProfilePage />} />
                  <Route path="additional-information" element={<ContractorAdditionalInformationPage />} />
                  <Route path="partners" element={<ComingSoonPage />} />
                  <Route path="billing" element={<ComingSoonPage />} />
                  <Route path="password" element={<ContractorPasswordPage />} />
                  <Route path="integrations" element={<ComingSoonPage />} />
                  <Route path="themes" element={<ContractorThemePage />} />
                  <Route path="notification-settings" element={<ComingSoonPage />} />
                  <Route path="*" element={<Navigate to={getDefaultAccountSettingsPath(userRole)} replace />} />
                </Route>
                <Route path="messages" element={<Navigate to="center" replace />} />
                <Route path="master-pass" element={<MasterPass />} />
                <Route path="messages/*">
                  <Route path="center" element={<MessageCenter />} />
                  <Route path="history" element={<MessageHistory />} />
                  <Route path="report" element={<MessageReport />} />
                  <Route element={<MessageTemplateContainerLayout />}>
                    <Route path="create-template" element={<MessageTemplateCreate />} />
                    <Route path="edit-template" element={<MessageTemplateEdit />} />
                    <Route path="add-template" element={<MessageTemplateAdd />} />
                  </Route>
                </Route>
              </Route>
            )}

          {[UserRole.ContractorManager, UserRole.ContractorAdmin].includes(userRole) &&
            !isSubscriptionPlanRestricted && (
              <Route element={<PrivateRoute />}>
                <Route path="users" element={<ContractorUsers />} />
              </Route>
            )}

          {userRole === UserRole.ContractorTechnician && !isSubscriptionPlanRestricted && (
            <Route element={<PrivateRoute />}>
              <Route path="customers/*" element={<Customers />} />
              <Route path="intelligence-reports" element={<IntelligenceReportsPage />} />
              <Route path="intelligence-reports/:reportId/full" element={<FullIntelligenceReport />} />
              <Route path="engagement-report" element={<MonthlyEngagementPage />} />
              <Route path="ad-hoc-reports" element={<AdHocReportsPage />} />
              <Route path="reports/:deviceSN" element={<ReportRedirectBySn />} />
              <Route path="monitoring" element={<MonitoringPage />} />
              <Route path="monitoring/alert-rules" element={<AlertRulesPage />} />
              <Route path="alerts" element={<AlertsPage />} />
              <Route path="master-pass" element={<MasterPass />} />
              <Route path="notifications" element={<NotificationsPage />} />
            </Route>
          )}

          {/* VENDOR ADMIN */}
          {userRole === UserRole.VendorAdmin && (
            <Route element={<PrivateRoute />}>
              <Route path="contractors" element={<Contractors />} />
              <Route path="warehouse-list" element={<WarehouseList />} />
              <Route path="calls" element={<Calls />} />
              <Route path="orders" element={<VOrders />}></Route>
              <Route path="devices" element={<VendorDevices />} />
              <Route path="devices/sensors" element={<VendorSensors />} />
              <Route path="alerts" element={<VendorAlerts />} />
              <Route path="dashboard" element={<VendorDashboard />} />
              <Route path="referrals" element={<VendorReferrals />} />
              <Route path="master-pass" element={<MasterPass />} />
              <Route path="messages" element={<Navigate to="center" replace />} />
              <Route path="messages/center" element={<VendorMessageCenter />} />
              <Route path="messages/history" element={<VendorMessageHistory />} />
              <Route path="messages/scheduled" element={<VendorScheduledMessages />} />
              <Route path="messages/report" element={<VendorMessageReport />} />
              <Route path="messages/create-template" element={<VendorMessageTemplate />} />
              <Route path="messages/edit-template" element={<VendorMessageTemplate />} />
              <Route path="admin/*">
                <Route path="users" element={<Users />} />
              </Route>
              <Route path="admin" element={<Navigate to="users" replace />} />
            </Route>
          )}

          {/* SALES MANAGER */}
          {userRole === UserRole.SalesManager && (
            <Route element={<PrivateRoute />}>
              <Route path="contractors" element={<Contractors />} />
              <Route path="admin" element={<Navigate to="users" replace />} />
              <Route path="orders" element={<VOrders />}></Route>
              <Route path="devices" element={<VendorDevices />} />
              <Route path="devices/sensors" element={<VendorSensors />} />
              <Route path="alerts" element={<VendorAlerts />} />
              <Route path="dashboard" element={<VendorDashboard />} />
              <Route path="referrals" element={<VendorReferrals />} />
              <Route path="master-pass" element={<MasterPass />} />
            </Route>
          )}

          {/* REFERRAL AGENT */}
          {userRole === UserRole.ReferralAgent && (
            <Route element={<PrivateRoute />}>
              <Route path="dashboard" element={<ReferralAgentDashboard />} />
              <Route path="referrals" element={<MyReferrals />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          )}

          {/* WAREHOUSE MANAGER */}
          {userRole === UserRole.WarehouseManager && (
            <Route element={<PrivateRoute />}>
              <Route path="wh-orders" element={<WHOrders />} />
              <Route path="wh-devices" element={<WHDevices />} />
            </Route>
          )}

          <Route element={<PrivateRoute />}>
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/" element={<RedirectPage />} />
          <Route path="/mydashboard" element={<Navigate to="/" replace />} />
          <Route path="*" element={<RedirectPage />} />
        </Route>
      </Routes>
    </MainLayout>
  )

  return (
    <Routes>
      <Route path="oauth/*" element={<OAuthApp />} />
      <Route path="*" element={mainApp} />
    </Routes>
  )
}
